import { FEATURE_SEPARATOR } from "@protontech/autofill/constants/features";
import { formCandidateSelector } from "@protontech/autofill/constants/selectors";
import { computeFeatures } from "@protontech/autofill/features/feature";
import type { FeatureProvider } from "@protontech/autofill/features/v1";
import type { AnyRule, DetectionClass, FeatureComputer, FieldClass, FormClass } from "@protontech/autofill/types";
import { FieldType, FormType } from "@protontech/autofill/types";
import { isCCInputField, isIdentity, maybeEmail, maybeHiddenUsername, maybeOTP, maybePassword, maybeUsername } from "@protontech/autofill/features/v1/field-predicates";
import { isCCSelectField } from "@protontech/autofill/utils/credit-card";
import { featureScore, isNoopForm, outRuleWithCache, processFieldEffect, processFormEffect, withFnodeEl } from "@protontech/autofill/utils/fathom";
import { isClassifiableField } from "@protontech/autofill/utils/field";
import { isClassifiable } from "@protontech/autofill/utils/flags";
import { overrides } from "@protontech/autofill/utils/overrides";
import { isVisibleForm } from "@protontech/autofill/utils/visible";
import { dom, out, rule, type } from "@protontech/fathom";

const getRulesForFieldClass = (klass: FieldClass, featureComputer: FeatureComputer) => [
  rule(type(`${klass}-field`), type(klass), {}),
  ...featureComputer.features.map((featName) => rule(type(klass), featureScore(`${klass}-field`, featName), { name: `${klass}${FEATURE_SEPARATOR}${featName}` })),
  ...outRuleWithCache("field-candidate", klass),
];

const getRulesForFormClass = (klass: FormClass, featureComputer: FeatureComputer) => [
  rule(type("form"), type(klass), {}),
  ...featureComputer.features.map((featName) => rule(type(klass), featureScore("form", featName), { name: `${klass}${FEATURE_SEPARATOR}${featName}` })),
  ...outRuleWithCache("form-candidate", klass),
];

const noopRules = [
  /* all undetected forms land in the `noop` class */
  rule(type("form").when(isNoopForm), type(FormType.NOOP), {}),
  ...outRuleWithCache("form-candidate", FormType.NOOP, () => () => true),
] as const;

const identityRules = [rule(type("field").when(isIdentity), type("identity-field"), {}), rule(type("identity-field"), type(FieldType.IDENTITY), {}), ...outRuleWithCache("field-candidate", FieldType.IDENTITY, () => () => true)] as const;

const ccRules = [
  rule(type("field").when(isCCInputField), type("cc-field"), {}),
  rule(type("field-candidate").when(isCCSelectField), type("cc-field"), {}),
  rule(type("cc-field"), type(FieldType.CREDIT_CARD), {}),
  ...outRuleWithCache("field-candidate", FieldType.CREDIT_CARD, () => () => true),
];

export const getBaseRules = (featureProvider: FeatureProvider) =>
  [
    rule(dom(formCandidateSelector), type("form-candidate"), {}),
    rule(dom("input"), type("field-candidate"), {}),
    ...overrides,

    /* The `form` type will contain form elements of interests and clusters
     * of inputs. Form candidates will be flagged as `processed` before output.
     * Only try to classify forms which are visible */
    rule(type("form-candidate").when(withFnodeEl(isClassifiable)), type("form-element"), {}),
    rule(
      type("form-element").when(withFnodeEl(isVisibleForm)),
      type("form").note((fnode) => computeFeatures(featureProvider.form, fnode)),
      {},
    ),
    rule(type("form-element"), out("form").through(processFormEffect), {}),
    ...noopRules /* all undetected forms land in the `noop` class */,

    /* The `field` type will contain all inputs of interests. Only visible
     * & active inputs will be flagged as processed on output  */
    rule(
      type("field-candidate").when(isClassifiableField),
      type("field").note((fnode) => computeFeatures(featureProvider.field, fnode)),
      {},
    ),
    rule(
      type("field").when(maybeUsername),
      type("username-field").note((fnode) => computeFeatures(featureProvider.username, fnode)),
      {},
    ),
    rule(
      type("field").when(maybeHiddenUsername),
      type("username-hidden-field").note((fnode) => computeFeatures(featureProvider["username-hidden"], fnode)),
      {},
    ),
    rule(
      type("field").when(maybeEmail),
      type("email-field").note((fnode) => computeFeatures(featureProvider.email, fnode)),
      {},
    ),
    rule(
      type("field").when(maybePassword),
      type("password-field").note((fnode) => computeFeatures(featureProvider.password, fnode)),
      {},
    ),
    rule(
      type("field").when(maybePassword),
      type("new-password-field").note((fnode) => computeFeatures(featureProvider["new-password"], fnode)),
      {},
    ),
    rule(
      type("field").when(maybeOTP),
      type("otp-field").note((fnode) => computeFeatures(featureProvider.otp, fnode)),
      {},
    ),
    ...identityRules /* identity is not a DetectionClass */,
    ...ccRules /* credit-card is not a DetectionClass */,

    rule(type("field"), out("field").through(processFieldEffect), {}),
  ] as const;

export const getRulesProvider = (featureProvider: FeatureProvider): Record<DetectionClass, readonly AnyRule[]> => {
  return {
    // fields
    email: getRulesForFieldClass("email", featureProvider.email),
    otp: getRulesForFieldClass("otp", featureProvider.otp),
    password: getRulesForFieldClass("password", featureProvider.password),
    "new-password": getRulesForFieldClass("new-password", featureProvider["new-password"]),
    "username-hidden": getRulesForFieldClass("username-hidden", featureProvider["username-hidden"]),
    username: getRulesForFieldClass("username", featureProvider.username),
    // forms
    login: getRulesForFormClass("login", featureProvider.login),
    "password-change": getRulesForFormClass("password-change", featureProvider["password-change"]),
    recovery: getRulesForFormClass("recovery", featureProvider.recovery),
    register: getRulesForFormClass("register", featureProvider.register),
  };
};
